import { paymentApi, westpacApi } from 'scenes/common/api';
import {
  marshalBindCreditCardPayment,
  marshalBindDirectDebitPayment,
} from 'scenes/common/api/quote/bind/marshaller';
import {
  CreditCardDetails,
  DirectDebitDetails,
} from 'scenes/common/flows/paymentDetails/types';
import connector from 'api/http';
import config from 'shared/config';
import { WireStatus, WireTransactionType } from 'scenes/common/api/quote/types';
import { ApiState } from 'scenes/common/types';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { updatePayment } from 'scenes/payments/redux/payment/paymentActions';
import {
  updateError,
  updateLoadingStatus,
} from 'scenes/payments/redux/payment/thunks';
import {
  BindResponse,
  ErrorPayload,
  PaymentState,
} from 'scenes/payments/types';
import { ApiError } from 'api/http/types';
import {
  toInsuranceName,
  toOtherDetails,
  toPaymentDetails,
} from './marshaller';
import { PaymentResult } from './types';

const server = connector(config.baseUrls.BFF_BASE_URL);

const storeApiError = (
  error: ApiError<ErrorPayload>,
  dispatch: ThunkDispatch<PaymentState, void, Action>,
  callingContext: string
) => {
  dispatch(
    updateError({
      error,
      errorOccuredAt: callingContext,
    })
  );
  dispatch(updateLoadingStatus(ApiState.Error));
};

const storeBindError = (
  error: ApiError<ErrorPayload>,
  dispatch: ThunkDispatch<PaymentState, void, Action>
) => {
  dispatch(
    updateError({
      error,
      errorOccuredAt: 'Quote Bind API',
    })
  );
  // Differentiate business errors from others.
  // Business errors will result in modal, others
  // system error.
  if (error.name === ApiState.BusinessError) {
    dispatch(updateLoadingStatus(ApiState.BusinessError));
  } else {
    dispatch(updateLoadingStatus(ApiState.Error));
  }
};

export const payByCreditCard = async (
  paymentState: PaymentState,
  creditCardDetails: CreditCardDetails,
  dispatch: ThunkDispatch<PaymentState, void, Action>
) => {
  const insuranceName = toInsuranceName(paymentState.product);
  const policyNumber = paymentState.policy.number;
  const policyReference = paymentState.policy.reference;
  const paymentDetails = toPaymentDetails(
    paymentState,
    creditCardDetails.cardType
  );

  let paymentApiData: { supplierBusinessCode: string; token: string };
  let westpacApiData: { singleUseTokenId: string };

  try {
    paymentApiData = await paymentApi().getToken();
  } catch (err) {
    storeApiError(err, dispatch, 'Payment gateway API');
    throw new Error(`Payment gateway error: ${err.message}`);
  }

  try {
    westpacApiData = await westpacApi().getSingleUseToken(
      paymentApiData.supplierBusinessCode,
      atob(paymentApiData.token),
      creditCardDetails
    );
  } catch (err) {
    storeApiError(err, dispatch, 'Westpac API');
    throw new Error(`Westpac api: ${err.message}`);
  }

  const request = marshalBindCreditCardPayment(
    insuranceName,
    policyNumber,
    policyReference,
    paymentDetails,
    westpacApiData.singleUseTokenId
  );

  request.targetStatus = WireStatus.ConfirmPayment;
  request.transactionType = WireTransactionType.Policy;

  const finalRequest = {
    ...request,
    otherDetails: toOtherDetails(paymentState),
  };

  try {
    const bindResponse: BindResponse = await server.post(
      '/policy/bind',
      finalRequest
    );
    // save receipt to state
    const payment = {
      ...paymentState.payment,
      receipt: bindResponse.payload.receiptNumber,
    };

    dispatch(updatePayment({ payment }));
    return PaymentResult.Success;
  } catch (err) {
    storeBindError(err, dispatch);
    if (err.name === ApiState.BusinessError) {
      return PaymentResult.Unsuccessful;
    }
    throw new Error(`payment api error: ${err.message}`);
  }
};

export const payByDirectDebit = async (
  paymentState: PaymentState,
  directDebitDetails: DirectDebitDetails,
  dispatch: ThunkDispatch<PaymentState, void, Action>
) => {
  const insuranceName = toInsuranceName(paymentState.product);
  const policyNumber = paymentState.policy.number;
  const policyReference = paymentState.policy.reference;
  const paymentDetails = toPaymentDetails(paymentState);

  const request = marshalBindDirectDebitPayment(
    insuranceName,
    policyNumber,
    policyReference,
    paymentDetails,
    directDebitDetails
  );

  request.targetStatus = WireStatus.ConfirmPayment;
  request.transactionType = WireTransactionType.Policy;

  const finalRequest = {
    ...request,
    otherDetails: toOtherDetails(paymentState),
  };

  try {
    await server.post('/policy/bind', finalRequest);

    return PaymentResult.Success;
  } catch (err) {
    storeBindError(err, dispatch);
    if (err.name === ApiState.BusinessError) {
      return PaymentResult.Unsuccessful;
    }
    throw new Error(`payment api error: ${err.message}`);
  }
};
